import { getSearchSocket } from 'search/initialiseSearchSocket'
import { SocketEventHandlers } from '@luxuryescapes/contract-search'
import { Socket } from 'socket.io-client'

interface LastSocketMessage<T extends keyof SocketEventHandlers> {
  event: T
  args: Parameters<SocketEventHandlers[T]>
}
/**
 * The socket instance that has the reconnect handler registered.
 */
let registeredSocket: Socket<SocketEventHandlers> | undefined = undefined

/**
 * Map of last socket messages by sessionId. Will be resent on reconnection if connection was lost.
 */
const lastMessages = new Map<string, LastSocketMessage<keyof SocketEventHandlers>>()

/**
 * Track whether we've been disconnected. Used to determine if we should resend on reconnect.
 */
let hasBeenDisconnected = false

/**
 * Register reconnect handler to resend last message when connection is restored.
 * This ensures that if a message was sent but the connection was lost before receiving
 * the completion event, the message will be resent automatically.
 */
export function registerReconnectHandler(socket: Socket<SocketEventHandlers>) {
  // Skip if we've already registered on this socket instance
  if (registeredSocket === socket) return

  if (socket.connected) {
    markConnected()
  }

  socket.on('connect', markConnected)
  socket.on('disconnect', markDisconnected)

  registeredSocket = socket
}

/**
 * Emit a socket message. The message will be automatically resent if the connection
 * is lost before the completion event is received.
 *
 * @param sessionId - The session ID (searchId or typeaheadId) to associate with this message
 * @param event - The event name to emit
 * @param args - The arguments to emit
 */
export function emitSocketMessage<T extends keyof SocketEventHandlers>(
  sessionId: string,
  event: T,
  ...args: Parameters<SocketEventHandlers[T]>
): void {
  const socket = getSearchSocket()

  // Store the last message for potential resend, keyed by sessionId
  lastMessages.set(sessionId, { event, args })

  // Emit the message
  socket.emit(event, ...args)
}

/**
 * Clear the last message for a specific sessionId. This should be called when the corresponding end/complete event is received.
 * @param sessionId - The session ID (searchId or typeaheadId) to clear the message for
 */
export function clearLastMessage(sessionId: string): void {
  lastMessages.delete(sessionId)
}

// --- Internal helper functions ---

function markConnected(): void {
  if (hasBeenDisconnected) {
    resendLastMessage()
  }
  hasBeenDisconnected = false
}

function markDisconnected(): void {
  hasBeenDisconnected = true
}

function resendLastMessage(): void {
  if (lastMessages.size === 0) return

  // Resend all pending messages
  const socket = getSearchSocket()
  for (const lastMessage of lastMessages.values()) {
    socket.emit(lastMessage.event, ...lastMessage.args)
  }
}
